import {
  WorkflowStepUserResponses,
  type DocumentDetailsRelease,
  type DocumentReleaseDetails,
  type WorkflowStep,
} from "~/api/types";
import { DocumentArtifactStatus } from "~/config/types";

type ReleaseWithArtifactStatus = {
  artifact: Pick<DocumentDetailsRelease["artifact"], "status">;
};
type ReleaseWithPermissions = {
  artifact: Pick<DocumentDetailsRelease["artifact"], "permissions">;
};
type ReleaseWithStatusAndPermissions = ReleaseWithArtifactStatus &
  ReleaseWithPermissions;

interface WorkflowStepControlOptions {
  canManage: boolean;
  canPublish: boolean;
  isWorkflowStarted: boolean;
  isActiveStep: boolean;
  isFirstStep: boolean;
  isLastStep: boolean;
  artifactStatus: DocumentArtifactStatus;
}

export const isDraftArtifact = (release: ReleaseWithArtifactStatus): boolean =>
  release.artifact.status === DocumentArtifactStatus.draft;

export const isPublishedArtifact = (
  release: ReleaseWithArtifactStatus,
): boolean => release.artifact.status === DocumentArtifactStatus.published;

const isArchivedArtifact = (release: ReleaseWithArtifactStatus): boolean =>
  release.artifact.status === DocumentArtifactStatus.archived;

const isDocumentArchived = (documentArchivedAt: string | null): boolean =>
  documentArchivedAt !== null;

export const isDocumentOrArtifactArchived = (
  release: ReleaseWithArtifactStatus,
  documentArchivedAt: string | null,
): boolean =>
  isDocumentArchived(documentArchivedAt) || isArchivedArtifact(release);

export const canManageArtifact = (release: ReleaseWithPermissions): boolean =>
  release.artifact.permissions.canManage;

export const canPublishArtifact = (release: ReleaseWithPermissions): boolean =>
  release.artifact.permissions.canPublish;

export const canEditDocumentContent = (
  release: ReleaseWithStatusAndPermissions,
  documentArchivedAt: string | null,
): boolean =>
  canManageArtifact(release) &&
  !isDocumentOrArtifactArchived(release, documentArchivedAt);

export const canEditDocumentMetadata = (
  release: ReleaseWithStatusAndPermissions,
  documentArchivedAt: string | null,
): boolean =>
  canManageArtifact(release) &&
  !isDocumentOrArtifactArchived(release, documentArchivedAt);

export const canArchiveDocument = (
  release: ReleaseWithPermissions,
  documentArchivedAt: string | null,
): boolean =>
  canManageArtifact(release) && !isDocumentArchived(documentArchivedAt);

export const canEditWorkflow = (
  release: ReleaseWithStatusAndPermissions,
): boolean => canManageArtifact(release) && isDraftArtifact(release);

export const canSetWorkflowAdoptionDate = (
  release: ReleaseWithStatusAndPermissions,
  isLastStepActive: boolean,
): boolean =>
  canManageArtifact(release) && (!isDraftArtifact(release) || isLastStepActive);

export const canInviteReaders = (
  release: ReleaseWithStatusAndPermissions,
): boolean => canManageArtifact(release) && !isArchivedArtifact(release);

export const canSeeWorkflowTab = (
  userId: number,
  release: ReleaseWithPermissions,
  releaseDetails?: DocumentReleaseDetails,
): boolean =>
  releaseDetails?.artifact.steps.some((step) =>
    step.users.some((u) => u.userId === userId),
  ) === true ||
  canManageArtifact(release) ||
  canPublishArtifact(release);

export const canSeeAnchoringTab = (
  release: ReleaseWithStatusAndPermissions,
): boolean => canManageArtifact(release) && !isDraftArtifact(release);

export const getApprovalStatus = (step: WorkflowStep) => {
  const totalCount = step.users.length;
  const approvedCount = step.users.filter(
    (u) => u.responseStatus === WorkflowStepUserResponses.approved,
  ).length;
  const isWaitingForApprovals =
    step.type === "approval" && approvedCount !== totalCount;

  return { isWaitingForApprovals, approvedCount, totalCount };
};

export const getWorkflowStepControlPermissions = ({
  canManage,
  canPublish,
  isWorkflowStarted,
  isActiveStep,
  isFirstStep,
  isLastStep,
  artifactStatus,
}: WorkflowStepControlOptions) => {
  const canStartWorkflow = canManage && !isWorkflowStarted && isFirstStep;
  const canMoveBackward =
    canManage && isActiveStep && !isFirstStep && isWorkflowStarted;
  const canMoveForward =
    canManage && isActiveStep && !isLastStep && isWorkflowStarted;
  const showPublishButton = canPublish && isActiveStep && isLastStep;

  const showControls =
    (canStartWorkflow || isActiveStep) &&
    artifactStatus === DocumentArtifactStatus.draft;

  return {
    canStartWorkflow,
    canMoveBackward,
    canMoveForward,
    showPublishButton,
    showControls,
  };
};
